import { DecisionDetail } from "../../models/home";
import { LanguageService } from "../language/language.service";
import type { DecisionTechnicalEntry } from "./home-decision-display-ux.service";

export class HomeDecisionTechnicalTraceBuilder {
  constructor(private readonly lang: LanguageService) {}

  getTechnicalEntries(detail: DecisionDetail | null): readonly DecisionTechnicalEntry[] {
    const decision = detail?.decision;
    if (!decision) return [];
    const entries: DecisionTechnicalEntry[] = Object.entries(decision.sourceTrace ?? {})
      .filter(([, value]) => !this.isEmptyTechnicalValue(value))
      .map(([key, value]) => ({ key: `trace.${key}`, label: this.getTraceLabel(key), value }));
    const signals: Array<{ key: string; label: string; value: unknown }> = [
      this.signal("priority", "score", decision.priority.score),
      this.signal("priority", "tier", decision.priority.tier),
      this.signal("priority", "finalReason", decision.priority.finalReason),
      this.signal("priority", "base", decision.priority.base),
      this.signal("priority", "urgency", decision.priority.urgency),
      this.signal("priority", "dateProximity", decision.priority.dateProximity),
      this.signal("priority", "riskBoost", decision.priority.riskBoost),
      this.signal("priority", "artifactBoost", decision.priority.artifactBoost),
      this.signal("priority", "userStateAdjustment", decision.priority.userStateAdjustment),
      this.signal("risk", "level", decision.risk.level),
      this.signal("risk", "category", decision.risk.category),
      this.signal("risk", "reason", decision.risk.reason),
      this.signal("risk", "impactAreas", decision.risk.impactAreas),
      this.signal("risk", "escalationTriggers", decision.risk.escalationTriggers),
      this.signal("risk", "evidenceIds", decision.risk.evidenceIds),
      this.signal("confidence", "overall", decision.confidence.overall),
      this.signal("confidence", "level", decision.confidence.level),
      this.signal("confidence", "classification", decision.confidence.classification),
      this.signal("confidence", "dateExtraction", decision.confidence.dateExtraction),
      this.signal("confidence", "actionSelection", decision.confidence.actionSelection),
      this.signal("confidence", "explanation", decision.confidence.explanation),
      this.signal("confidence", "warnings", decision.confidence.warnings)
    ];
    entries.push(...signals.filter((entry) => !this.isEmptyTechnicalValue(entry.value)));
    return entries;
  }

  getTraceLabel(key: string): string {
    const keys: Record<string, string> = {
      traceId: "home.decisionDetail.trace.traceId", actionSource: "home.decisionDetail.trace.actionSource",
      actionSourceDetail: "home.decisionDetail.trace.actionSourceDetail", confidenceSource: "home.decisionDetail.trace.confidenceSource",
      confidenceSourceDetail: "home.decisionDetail.trace.confidenceSourceDetail", explanationSource: "home.decisionDetail.trace.explanationSource",
      explanationSourceDetail: "home.decisionDetail.trace.explanationSourceDetail",
      catalogHealthAtEvaluation: "home.decisionDetail.trace.catalogHealthAtEvaluation",
      evaluatorVersion: "home.decisionDetail.trace.evaluatorVersion", rulesVersion: "home.decisionDetail.trace.rulesVersion"
    };
    return keys[key] ? this.lang.translate(keys[key]) : this.humanizeFieldName(key) || key;
  }
  getTechnicalSignalLabel(group: "priority" | "risk" | "confidence", field: string): string {
    return `${this.lang.translate(`home.decisionDetail.${group}`)} · ${this.getTechnicalFieldLabel(field)}`;
  }
  getTechnicalFieldLabel(field: string): string {
    return this.translateKnownKey(`home.decisionDetail.technicalField.${this.normalizeTechnicalField(field)}`) ??
      this.humanizeTechnicalLabel(field);
  }
  isEmptyTechnicalValue(value: unknown): boolean {
    return value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
  }
  humanizeTechnicalLabel(label: string): string {
    return label.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/\s+/g, " ").trim();
  }
  normalizeTechnicalField(value: string): string {
    return value.replace(/([a-z0-9])([A-Z])/g, "$1_$2").replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "").toLowerCase();
  }
  translateKnownKey(key: string): string | null {
    const translated = this.lang.translate(key);
    return translated === key ? null : translated;
  }

  private signal(group: "priority" | "risk" | "confidence", field: string, value: unknown) {
    return { key: `${group}.${field}`, label: this.getTechnicalSignalLabel(group, field), value };
  }
  private humanizeFieldName(value: string): string {
    const label = value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_.-]+/g, " ").replace(/\s+/g, " ").trim();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
  }
}
